import {
  GridApi,
  GridState,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GRID_TREE_DATA_GROUPING_FIELD,
} from '@mui/x-data-grid-premium';
import { MutableRefObject, useMemo } from 'react';

export const createStateChangeHandler = (
  apiRef: MutableRefObject<GridApi>,
  isGroupingEnabled: boolean
) => {
  return (state: GridState) => {
    if (state.pinnedColumns && apiRef.current) {
      const currentLeft = state.pinnedColumns.left || [];
      const protectedColumns = [GRID_CHECKBOX_SELECTION_COL_DEF.field];

      if (isGroupingEnabled) {
        protectedColumns.push(GRID_TREE_DATA_GROUPING_FIELD, 'group_name');
      }

      // Check if protected columns are in the correct order at the beginning
      let needsCorrection = false;
      for (let i = 0; i < protectedColumns.length; i++) {
        if (currentLeft[i] !== protectedColumns[i]) {
          needsCorrection = true;
          break;
        }
      }

      if (needsCorrection) {
        // Extract user columns by filtering out all protected columns
        const userColumns = currentLeft.filter(
          col => !protectedColumns.includes(col)
        );
        const correctedLeft = [...protectedColumns, ...userColumns];

        // Apply correction asynchronously to avoid infinite loops
        requestAnimationFrame(() => {
          if (apiRef.current) {
            apiRef.current.setPinnedColumns({
              left: correctedLeft,
              right: state.pinnedColumns?.right || [],
            });
          }
        });
      }
    }
  };
};

/**
 * Creates a filtered row selection model that only includes selections for currently visible rows.
 * This prevents MUI DataGrid from automatically clearing selections when the rows prop changes
 * due to search/filter operations.
 *
 * @param selectedRows - Array of selected row IDs (can be string[] or number[])
 * @param displayData - Array of rows currently displayed in the grid
 * @param getRowId - Function to extract the ID from a row object
 * @returns Selection model object for MUI DataGrid with only visible selections
 */
export function useFilteredSelectionModel<T>(
  selectedRows: (string | number)[],
  displayData: T[],
  getRowId: (row: T) => string | number
): { type: 'include'; ids: Set<string | number> } {
  return useMemo(() => {
    const displayedRowIds = new Set(displayData.map(getRowId));
    const visibleSelectedIds = selectedRows.filter(id =>
      displayedRowIds.has(id)
    );
    return {
      type: 'include' as const,
      ids: new Set(visibleSelectedIds),
    };
  }, [selectedRows, displayData, getRowId]);
}
